<template>
  <div id="rendered-service-page">
    <vx-card class="mb-base" card-border no-shadow v-if="service">
      <div slot="no-body">
        <open-street-map
          class="responsive card-map-top"
          view-only
          :markers="[{ latlng: [service.address.location.lat, service.address.location.lon] }]"
        />
      </div>

      <vs-row class="mb-base">
        <div class="vx-col w-full md:w-1/4 sm:w-1/2 mb-3">
          <h4>{{ $t(`places.categories.${service.category}`) }}</h4>
          <vs-row class="mt-3">{{ $t(`places.subjects.${service.subject}`) }}</vs-row>
        </div>
        <div class="vx-col w-full md:w-1/4 sm:w-1/2 mb-3">
          <vs-row class="font-semibold">{{ $t('entity.created_at') }}:</vs-row>
          <vs-row class="mt-3">{{ service.created_at | datetime }}</vs-row>
        </div>
        <div class="vx-col w-full md:w-1/4 sm:w-1/2 mb-3" v-if="service.date">
          <vs-row class="font-semibold">{{ $t('request_forms.request_datetime') }}:</vs-row>
          <vs-row class="mt-3" v-if="service.date">{{ service.date | datetime }}</vs-row>
          <vs-row class="mt-3" v-else>{{ $t('request_forms.for_now') }}</vs-row>
        </div>
        <div class="vx-col w-full md:w-1/4 sm:w-1/2 mb-3">
          <vs-row class="font-semibold">{{ $t('entity.state') }}:</vs-row>
          <vs-row class="mt-3"
            ><cell-badges :data="service.status ? service.status : 'pending'" local="services.status." />
          </vs-row>
        </div>
      </vs-row>
      <vs-row class="mb-base" v-if="service.status !== 'canceled'">
        <vx-card no-shadow card-border :subtitle="$t('invoices.provider')">
          <template v-if="service.details && service.status !== 'pending' && service.details.employeeUser">
            <vs-row class="mb-2">
              <div class="vx-col w-full md:w-1/4 sm:w-1/2 mb-3">
                <h4>
                  {{ $t(`users.genders.name.${service.details.employeeUser.details.gender}`) }}
                  {{ service.details.employeeUser.details.firstName }}
                  {{ service.details.employeeUser.details.lastName }}
                </h4>
              </div>
              <div class="vx-col w-full md:w-1/4 sm:w-1/2 mb-3">
                <vs-row class="font-semibold">{{ $t('invoices.employer') }}:</vs-row>
                <vs-row class="mt-3">{{ service.details.place.alias | persian_digits }}</vs-row>
              </div>
              <div class="vx-col w-full md:w-1/4 sm:w-1/2 mb-3">
                <vs-row class="font-semibold">{{ $t('users.phone') }}:</vs-row>
                <vs-row class="mt-3">{{ service.details.employeeUser.phone | persian_digits }}</vs-row>
              </div>
            </vs-row>
            <vs-row class="mb-2" v-if="service.commendation">
              <vs-row class="font-semibold">{{ $t('invoice.commendation') }}:</vs-row>
              <vs-row class="mt-3">{{ service.commendation }}</vs-row>
            </vs-row>
          </template>
          <template v-if="!service.status || service.status === 'pending'">
            {{ $t('services.determining_msg') }}
          </template>
          <template v-if="service.status === 'expired'">
            {{ $t('services.no_provider_found') }}
          </template>
        </vx-card>
      </vs-row>
      <vs-row class="mb-base">
        <vx-card no-shadow card-border :subtitle="$t('invoices.receiver')">
          <vs-row class="mb-2">
            <div class="vx-col w-full md:w-1/4 sm:w-1/2 mb-3">
              <h4>
                {{ $t(`users.genders.name.${service.gender}`) }} {{ service.firstName }}
                {{ service.lastName }}
              </h4>
            </div>
            <div class="vx-col w-full md:w-1/4 sm:w-1/2 mb-3">
              <vs-row class="font-semibold">{{ $t('users.phone') }}:</vs-row>
              <vs-row class="mt-3">{{ service.phoneNumber | persian_digits }}</vs-row>
            </div>
            <div class="vx-col w-full md:w-1/4 sm:w-1/2 mb-3">
              <vs-row class="font-semibold">{{ $t('users.emergency_phone') }}:</vs-row>
              <vs-row class="mt-3">{{ service.emergencyNumber | persian_digits }}</vs-row>
            </div>
          </vs-row>
          <vs-row class="mb-3">
            <div class="vx-col w-full"><address-string :address="service.address" /></div>
          </vs-row>
        </vx-card>
      </vs-row>
      <vs-row class="mb-base" v-if="service.details.reserve">
        <vx-card no-shadow card-border :subtitle="$t('reservation.reserved')">
          <vs-row class="mb-2">
            <div class="vx-col w-full md:w-1/3 sm:w-1/2 mb-3">
              <vs-row class="font-semibold">{{ $t('services.start_time') }}:</vs-row>
              <vs-row class="mt-3">{{ service.details.reserve.startDate | datetime }}</vs-row>
            </div>
            <div class="vx-col w-full md:w-1/3 sm:w-1/2 mb-3">
              <vs-row class="font-semibold">{{ $t('services.end_time') }}:</vs-row>
              <vs-row class="mt-3">{{ service.details.reserve.endDate | datetime }}</vs-row>
            </div>
          </vs-row>
        </vx-card>
      </vs-row>

      <vs-row class="mb-base" v-if="service.details.caseManagerUser">
        <vx-card no-shadow card-border :subtitle="$t('case_manager.case_manager')">
          <vs-row class="mb-2">
            <div class="vx-col w-full md:w-1/4 sm:w-1/2 mb-3">
              <h4>
                {{ $t(`users.genders.name.${service.details.caseManagerUser.details.gender}`) }}
                {{ service.details.caseManagerUser.details.firstName }}
                {{ service.details.caseManagerUser.details.lastName }}
              </h4>
            </div>
            <div class="vx-col w-full md:w-1/4 sm:w-1/2 mb-3">
              <vs-row class="font-semibold">{{ $t('users.phone') }}:</vs-row>
              <vs-row class="mt-3">{{ service.details.caseManagerUser.phone | persian_digits }}</vs-row>
            </div>
          </vs-row>
        </vx-card>
      </vs-row>
      <vs-row>
        <vs-row class="font-semibold">{{ $t('services.requested_services') }}</vs-row>
        <vs-row class="mt-3">
          <vs-table ref="table" :data="service.items" class="w-full">
            <template slot="thead">
              <vs-th>{{ $t('request_forms.service_name') }}</vs-th>
              <vs-th>{{ $t('request_forms.number_of_service') }} </vs-th>
            </template>
            <template slot-scope="{ data }">
              <tbody v-if="data.length">
                <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
                  <vs-td>
                    {{ $t(`health_services.${service.subject}.${tr.service}`) }}
                  </vs-td>
                  <vs-td> {{ tr.count | persian_digits }} {{ $t(`units.${tr.unit}`) }} </vs-td>
                </vs-tr>
              </tbody>
            </template>
          </vs-table>
        </vs-row>
      </vs-row>
      <div slot="footer" v-if="service.status">
        <vs-row vs-justify="flex-end">
          <vs-button
            type="border"
            class="mr-2"
            @click="$router.push({ name: 'invoice-view', params: { id: service.invoiceId } })"
            v-if="!['expired'].includes(service.status)"
          >
            {{ $t('invoices.view_invoice') }}
          </vs-button>
          <vs-button
            icon-pack="feather"
            icon="icon-x"
            color="danger"
            @click="openCancelConfirm(service)"
            v-if="['pending', 'accepted'].includes(service.status)"
          >
            {{ $t('invoices.cancel') }}
          </vs-button>
        </vs-row>
      </div>
    </vx-card>
  </div>
</template>

<script>
import AddressString from '@/views/components/maps/AddressString'
import OpenStreetMap from '@/views/components/maps/OpenStreetMap'
import CellBadges from '@/views/pages/system/businesses/business-list/CellBadges'

export default {
  name: 'ServiceView',
  components: { CellBadges, OpenStreetMap, AddressString },
  data() {
    return {
      service: null
    }
  },
  methods: {
    openCancelConfirm(request) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: this.$t('invoices.cancel'),
        text: this.$t('messages.are_you_sure'),
        accept: () => {
          this.$store
            .dispatch('request/updateRequestBySubject', {
              category: request.category,
              subject: request.subject,
              id: request.id,
              activity: 'UserCancellation',
              data: {}
            })
            .then(() => {
              this.service.state = 'canceled'
              this.$notifySuccess(this.$t('services.successfully_canceled'))
            })
        },
        acceptText: this.$t('button.yes'),
        cancelText: this.$t('button.no')
      })
    }
  },
  created() {
    this.$vs.loading()

    this.$store
      .dispatch('request/fetchHealthRequestById', this.$route.params.id)
      .then(res => {
        const request = JSON.parse(JSON.stringify(res.data))
        if (request.owner !== this.$store.state.AppActiveUser.id) this.$router.push('/')
        this.$store
          .dispatch('request/fetchRequestDetailsBySubject', {
            id: request.id,
            subject: request.subject,
            category: request.category
          })
          .then(res => {
            this.service = { ...request, details: res.data }
            this.$vs.loading.close()
          })
          .catch(() => this.$vs.loading.close())
      })
      .catch(() => this.$vs.loading.close())
  }
}
</script>

<style>
.card-map-top {
  overflow: hidden;
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;
}
</style>
